import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ServiceTypes } from "src/api/adminMemo/adminmemo-types";
import { ContractManageList } from "src/api/contract/contract-types";
import { AssignedContractModel, ContractGroupModel } from "src/api/contractGroup/contractgroup-type";
import { BaseButton, ContentsTitle } from "src/components";
import AdminMemoV3 from "src/components/AdminMemoV3/AdminMemoV3";
import GoToListButton from "src/components/GoToListButton";
import HistoryDetail from "src/components/histroy/HistoryDetail";
import RegistrationInformation from "src/components/RegistrationInformation";
import useNavigate from "src/hooks/usePartnerNavigate";
import { useTabNavigation } from "src/hooks/useTabNavigation";
import { PagePath } from "src/pages/product/details";
import { useModal } from "src/recoil/modalState/hook";
import { CONTRACT_GROUP_TABS } from "../contractgroup-constant";
import { useContractGroup } from "../hooks/useContractGroup";
import AssginedContractTable from "./components/AssginedContractTable";
import ContractGroupCustomFieldSection from "./components/ContractGroupCustomFieldSection";
import ContractGroupInfo from "./components/ContractGroupInfo";

const ContractGroupDetail = () => {
  const { id: groupId } = useParams();
  const navigate = useNavigate();
  const [contractGroup, setContractGroup] = useState<ContractGroupModel>();
  const [contractSummaryList, setContractSummaryList] = useState<AssignedContractModel[]>([]);
  const [addableContractIds, setAddableContractIds] = useState<string[]>([]);

  const { setBaseModal } = useModal();
  const { activeTab, clickTab } = useTabNavigation({
    tabs: CONTRACT_GROUP_TABS,
  });

  //계약 주체 정보
  const contractSubjectInfo = useMemo(() => {
    if (!contractSummaryList || contractSummaryList.length === 0) return null;

    // mbOrganizationId로 중복 제거하여 유니크한 조직들만 추출
    const uniqueOrgs = contractSummaryList.reduce((acc, contract) => {
      const orgId = contract.mbOrganizationId;
      const orgName = contract.orgName || "미지정";

      // 이미 해당 조직이 있는지 확인
      if (!acc.some((org) => org.mbOrganizationId === orgId)) {
        acc.push({
          mbOrganizationId: orgId,
          orgName,
          contractStartDate: contract.contractStartDate,
        });
      }

      return acc;
    }, [] as Array<{ mbOrganizationId: number; orgName: string; contractStartDate: string }>);

    // 가장 최근 contractStartDate를 가진 조직을 주 계약주체로 선정
    let primaryOrgName = "";
    let latestDate = new Date(0);

    uniqueOrgs.forEach((org) => {
      const contractDate = new Date(org.contractStartDate || 0);
      if (contractDate > latestDate) {
        latestDate = contractDate;
        primaryOrgName = org.orgName;
      }
    });

    const totalUniqueCount = uniqueOrgs.length;
    const otherCount = totalUniqueCount - 1; // 주 계약주체 제외한 나머지

    return {
      primaryOrgName,
      primaryCount: 1, // 주 계약주체는 항상 1개
      otherCount,
      totalCount: totalUniqueCount,
    };
  }, [contractSummaryList]);

  const {
    getContractGroupDetail, // 계약그룹 상세 조회
    getContractMapOfGroupList, // 계약그룹 계약매핑 조회
    addContractMapOfGroup, // 계약그룹 계약매핑 등록
    addContractGroup,
  } = useContractGroup();

  useEffect(() => {
    const fetchData = async () => {
      const contractGroupData = await getContractGroupDetail(groupId || "");

      setContractGroup(contractGroupData);
      await fetchContractMapOfGroup();
    };

    if (groupId) {
      fetchData();
    }
  }, [groupId]);

  // 계약그룹 계약매핑 목록 조회
  const fetchContractMapOfGroup = async () => {
    const contractMapOfGroupData = await getContractMapOfGroupList(groupId || "");
    setContractSummaryList(contractMapOfGroupData.contractListWithOrg);
    setAddableContractIds(contractMapOfGroupData.addableContractIds);
  };

  // 계약그룹 계약매핑 등록
  const handleAddContract = async (contractList: ContractManageList[]) => {
    const request = contractList.map((item) => ({
      contractId: String(item.contractId),
      groupId: groupId!,
    }));

    await addContractMapOfGroup(request);
    await fetchContractMapOfGroup();
  };

  const handleDeleteContractGroup = async () => {
    const execute = async () => {
      await addContractGroup({ ...contractGroup!, isDeleted: true });
      navigate(PagePath.contractGroup.list);
    };

    setBaseModal({
      isOpen: true,
      title: "삭제 하시겠습니까?",
      btnLeftTitle: "취소",
      btnRightTitle: "확인",
      onClick: () => {
        execute();
        setBaseModal({
          isOpen: false,
        });
      },
      onClose: () => {
        setBaseModal({
          isOpen: false,
        });
      },
    });
  };

  const handleDeleteContract = async (contract: AssignedContractModel) => {
    const execute = async () => {
      await addContractMapOfGroup([{ contractId: contract.contractGroupMap.contractId, groupId: groupId!, isDeleted: true }]);
      await fetchContractMapOfGroup();
    };

    setBaseModal({
      isOpen: true,
      title: "삭제 하시겠습니까?",
      btnLeftTitle: "취소",
      btnRightTitle: "확인",
      onClick: () => {
        execute();
        setBaseModal({
          isOpen: false,
        });
      },
      onClose: () => {
        setBaseModal({
          isOpen: false,
        });
      },
    });
  };

  return (
    <div>
      <ContentsTitle
        title=""
        tabs={CONTRACT_GROUP_TABS}
        activeTab={activeTab}
        clickTab={clickTab}
        detailInfo={[{ title: "계약그룹 ID", value: groupId || "" }]}
      />
      <div className="contents-container__scroll">
        {/* 기본정보 탭 */}
        {activeTab?.value === "" && contractGroup && (
          <div className="contents-container__wrap">
            <ContractGroupInfo contractGroup={contractGroup} contractSubjectInfo={contractSubjectInfo} />
            <ContractGroupCustomFieldSection groupCustomFields={contractGroup.inputList || []} />
            <AssginedContractTable
              contractSummaryList={contractSummaryList}
              handleAddContract={handleAddContract}
              handleDeleteContract={handleDeleteContract}
              addableContractIds={addableContractIds}
            />
            <RegistrationInformation data={contractGroup} />
            <AdminMemoV3 serviceId={Number(contractGroup?.groupId)} serviceType={ServiceTypes.SERVICE_CONTRACT_GROUP} />
          </div>
        )}
        {/* 변경내역 탭 (AL115, identifier.contractGroupId) */}
        {activeTab?.value === "history" && groupId && <HistoryDetail identifier={{ contractGroupId: groupId }} />}
      </div>
      <div className="contents-container__btn-wrap">
        <div className="left-area">
          <GoToListButton />
        </div>
        <div className="right-area">
          {contractGroup && contractSummaryList.length === 0 && (
            <BaseButton title={"삭제"} className="size-large color-white mr10" onClick={handleDeleteContractGroup} />
          )}
          <BaseButton
            title={"수정"}
            className="size-large"
            onClick={() => {
              navigate(PagePath.contractGroup.form + "?id=" + groupId);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ContractGroupDetail;
